// ===================================================
// GAM Command Center — the one layer scale
// ===================================================
//
// Gal, 2026-09-06: «יש הגדרה אוניברסלית שצריך לסדר במקום לרדוף אחרי זה
// כל הזמן ולסדר נקודתית» — after a block menu and the export dropdown both
// came up underneath the surfaces they were opened from.
//
// WHY IT KEPT HAPPENING. Before this file the app had four unrelated ladders
// that nobody could compare, measured 2026-09-06:
//
//   • vBlock's own scale          1 … 1001   — folded in 2026-09-06; the file
//                                              still exists, but every value
//                                              in it is now derived from here
//   • page chrome, ad hoc         40 … 200
//   • frames / fullscreen         9989 … 10020
//   • the editor's overlay        10040
//   • VClick, the dev tool        2147483000+ — folded in 2026-09-06
//   • the two other dev layers    9999 … 10003 — folded in 2026-09-06; they
//                                                sat BELOW `popover`, so a
//                                                product menu could cover the
//                                                very tool you debug it with
//
// A vBlock context menu asked for 100 and honestly believed that was high.
// It lost to a frame at 10020 and to chrome at 1000 — not because anyone
// chose that, but because the two numbers were written months apart by
// people who could not see each other's ladder.
//
// THE RULE, in one line: nothing in normal page flow may exceed `appCeiling`,
// and every transient popup goes ABOVE it through a portal.
//
// z-index alone is not enough and never was. An ancestor with `transform`,
// `filter`, `backdrop-filter` or `will-change` opens a stacking context that
// a descendant cannot climb out of at any z-index, and an ancestor with
// `overflow: hidden|auto` simply clips it. Both were happening here: the
// export dropdown was trapped under a frame, the block menu was cut off by
// the editor's own scroll container. The portal in <OverlayLayer> is what
// actually fixes those two; the numbers below only settle who wins once
// everyone is a direct child of <body>.
//
// The same numbers exist as CSS custom properties in globals.css
// (--z-popover and friends) so stylesheets — including packages/editor's,
// which cannot import this file — land on the same ladder.

export const Z = {
  /** Normal document flow. */
  base: 0,
  /** A card lifted on hover, a dragged item. */
  raised: 10,
  /** Sticky table headers, in-page toolbars. */
  sticky: 100,
  /** Sidebar, docks, page headers — persistent app chrome. */
  chrome: 1000,

  /**
   * The highest surface that still lives in normal flow: a fullscreen
   * embedded app frame (EmbeddedAppFrame). MEASURED, not chosen — this is
   * the number the app already had. Nothing in flow may go above it.
   */
  appCeiling: 10020,

  /** Dropdowns, context menus, block menus, floating toolbars. Portalled. */
  popover: 10040,
  /** Dialogs and their backdrop. Portalled. */
  modal: 10060,
  /** Toasts — must be readable even over a modal. Portalled. */
  toast: 10080,
  /** Tooltips — the last thing drawn, never interactive. Portalled. */
  tooltip: 10090,

  /**
   * VClick, InspectOverlay and DesignCaptureLayer: the on-screen dev tools,
   * deliberately above the whole product so they can be used to debug it.
   * Named here so the value stops being an unexplained magic number in a
   * component file.
   *
   * A dev tool orders its own parts with small offsets off this one
   * (`Z.devTool + 1`, `+ 2`, …) — never with a second raw number.
   */
  devTool: 2147483000,
} as const;

export type ZLayer = keyof typeof Z;
